import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, startOfQuarter, endOfQuarter } from 'date-fns';
import { accountingApi, ProfitLossData, BalanceSheetData, FinancialReportData } from '../services/accounting';
import { formatCurrency } from '../utils/currency';
import { useCompany } from '../contexts/CompanyContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Accounting: React.FC = () => {
  const { baseCurrency } = useCompany();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Profit & Loss state
  const [plStartDate, setPlStartDate] = useState<Date>(startOfMonth(new Date()));
  const [plEndDate, setPlEndDate] = useState<Date>(endOfMonth(new Date()));
  const [plData, setPlData] = useState<ProfitLossData | null>(null);

  // Balance Sheet state
  const [bsAsOfDate, setBsAsOfDate] = useState<Date>(new Date());
  const [bsData, setBsData] = useState<BalanceSheetData | null>(null);

  // Reports state
  const [reportType, setReportType] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [reportStartDate, setReportStartDate] = useState<Date>(startOfMonth(new Date()));
  const [reportEndDate, setReportEndDate] = useState<Date>(endOfMonth(new Date()));
  const [reportData, setReportData] = useState<FinancialReportData | null>(null);

  const loadProfitLoss = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountingApi.getProfitLoss(
        format(plStartDate, 'yyyy-MM-dd'),
        format(plEndDate, 'yyyy-MM-dd')
      );
      setPlData(data);
    } catch (err: any) {
      console.error('Error loading profit & loss:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load profit & loss statement';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [plStartDate, plEndDate]);

  const loadBalanceSheet = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountingApi.getBalanceSheet(format(bsAsOfDate, 'yyyy-MM-dd'));
      setBsData(data);
    } catch (err: any) {
      console.error('Error loading balance sheet:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load balance sheet';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [bsAsOfDate]);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountingApi.getReports(
        reportType,
        format(reportStartDate, 'yyyy-MM-dd'),
        format(reportEndDate, 'yyyy-MM-dd')
      );
      setReportData(data);
    } catch (err: any) {
      console.error('Error loading reports:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load financial reports';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [reportType, reportStartDate, reportEndDate]);

  const handleQuickDateRange = (range: 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear') => {
    const now = new Date();
    switch (range) {
      case 'thisMonth':
        setPlStartDate(startOfMonth(now));
        setPlEndDate(endOfMonth(now));
        break;
      case 'lastMonth':
        const lastMonth = subMonths(now, 1);
        setPlStartDate(startOfMonth(lastMonth));
        setPlEndDate(endOfMonth(lastMonth));
        break;
      case 'thisQuarter':
        setPlStartDate(startOfQuarter(now));
        setPlEndDate(endOfQuarter(now));
        break;
      case 'thisYear':
        setPlStartDate(startOfYear(now));
        setPlEndDate(endOfYear(now));
        break;
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      loadProfitLoss();
    } else if (tabValue === 1) {
      loadBalanceSheet();
    } else if (tabValue === 2) {
      loadReports();
    }
  }, [tabValue, loadProfitLoss, loadBalanceSheet, loadReports]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Accounting Reports
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Profit & Loss" />
          <Tab label="Balance Sheet" />
          <Tab label="Financial Reports" />
        </Tabs>
      </Paper>

      {/* Profit & Loss Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={format(plStartDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setPlStartDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={format(plEndDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setPlEndDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('thisMonth')}>
                  This Month
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('lastMonth')}>
                  Last Month
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('thisQuarter')}>
                  This Quarter
                </Button>
                <Button size="small" variant="outlined" onClick={() => handleQuickDateRange('thisYear')}>
                  This Year
                </Button>
                <Button size="small" variant="contained" onClick={loadProfitLoss} disabled={loading}>
                  Generate Report
                </Button>
              </Box>
            </Grid>
          </Grid>

          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {plData && !loading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Profit & Loss Statement
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Period: {format(new Date(plData.period.startDate), 'MMM d, yyyy')} -{' '}
                {format(new Date(plData.period.endDate), 'MMM d, yyyy')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Revenue
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                          {formatCurrency(plData.revenue, baseCurrency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ pl: 2 }}>
                          Expenses
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Operating Expenses</TableCell>
                      <TableCell align="right">{formatCurrency(plData.expenses.operating, baseCurrency)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Payroll</TableCell>
                      <TableCell align="right">{formatCurrency(plData.expenses.payroll, baseCurrency)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>Depreciation</TableCell>
                      <TableCell align="right">{formatCurrency(plData.expenses.depreciation, baseCurrency)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Total Expenses
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold">
                          {formatCurrency(plData.expenses.total, baseCurrency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="h6" fontWeight="bold">
                          Net Profit / Loss
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={plData.profit >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(plData.profit, baseCurrency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Profit Margin</TableCell>
                      <TableCell align="right">
                        <Typography color={plData.profitMargin >= 0 ? 'success.main' : 'error.main'}>
                          {plData.profitMargin.toFixed(2)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* Balance Sheet Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="As Of Date"
                type="date"
                value={format(bsAsOfDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setBsAsOfDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Button variant="contained" onClick={loadBalanceSheet} disabled={loading}>
                Generate Balance Sheet
              </Button>
            </Grid>
          </Grid>

          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {bsData && !loading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Balance Sheet
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                As of: {format(new Date(bsData.asOfDate), 'MMM d, yyyy')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Assets
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Cash</TableCell>
                            <TableCell align="right">{formatCurrency(bsData.assets.cash, baseCurrency)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Accounts Receivable</TableCell>
                            <TableCell align="right">
                              {formatCurrency(bsData.assets.accountsReceivable, baseCurrency)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ pl: 0 }}>
                                Fixed Assets
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ pl: 4 }}>Fixed Assets (at Cost)</TableCell>
                            <TableCell align="right">
                              {formatCurrency(bsData.assets.fixedAssets.atCost, baseCurrency)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ pl: 4 }}>Less: Accumulated Depreciation</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              ({formatCurrency(bsData.assets.fixedAssets.accumulatedDepreciation, baseCurrency)})
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ pl: 2 }}>
                              <Typography variant="body2" fontWeight="bold">
                                Fixed Assets (Net)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(bsData.assets.fixedAssets.net, baseCurrency)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Total Assets
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {formatCurrency(bsData.assets.total, baseCurrency)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Liabilities & Equity
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell>Accounts Payable</TableCell>
                            <TableCell align="right">
                              {formatCurrency(bsData.liabilities.accountsPayable, baseCurrency)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Total Liabilities
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {formatCurrency(bsData.liabilities.total, baseCurrency)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Retained Earnings</TableCell>
                            <TableCell align="right">
                              {formatCurrency(bsData.equity.retainedEarnings, baseCurrency)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Total Equity
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle2" fontWeight="bold">
                                {formatCurrency(bsData.equity.total, baseCurrency)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Typography variant="h6" fontWeight="bold">
                                Total Liabilities & Equity
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" fontWeight="bold">
                                {formatCurrency(bsData.liabilitiesAndEquity, baseCurrency)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box mt={3} p={2} bgcolor={bsData.total === bsData.liabilitiesAndEquity ? 'success.light' : 'error.light'}>
                <Typography variant="body2" align="center">
                  {bsData.total === bsData.liabilitiesAndEquity
                    ? '✓ Balance Sheet is balanced'
                    : '⚠ Balance Sheet does not balance. Please review the calculations.'}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* Financial Reports Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Report Type"
                value={reportType}
                onChange={(e) => setReportType(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={format(reportStartDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setReportStartDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={format(reportEndDate, 'yyyy-MM-dd')}
                onChange={(e) => e.target.value && setReportEndDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="contained" onClick={loadReports} disabled={loading}>
                Generate Report
              </Button>
            </Grid>
          </Grid>

          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {reportData && !loading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Financial Report - {reportData.reportType.charAt(0).toUpperCase() + reportData.reportType.slice(1)}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Period: {format(new Date(reportData.period.startDate), 'MMM d, yyyy')} -{' '}
                {format(new Date(reportData.period.endDate), 'MMM d, yyyy')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Category
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold">
                          Amount
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Revenue</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(
                          typeof reportData.summary.revenue === 'string'
                            ? parseFloat(reportData.summary.revenue)
                            : reportData.summary.revenue,
                          baseCurrency
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Operating Expenses</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(
                          typeof reportData.summary.expenses === 'string'
                            ? parseFloat(reportData.summary.expenses)
                            : reportData.summary.expenses,
                          baseCurrency
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Payroll</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(
                          typeof reportData.summary.payroll === 'string'
                            ? parseFloat(reportData.summary.payroll)
                            : reportData.summary.payroll,
                          baseCurrency
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Total Expenses
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'error.main' }}>
                          {formatCurrency(
                            (typeof reportData.summary.expenses === 'string'
                              ? parseFloat(reportData.summary.expenses)
                              : reportData.summary.expenses) +
                              (typeof reportData.summary.payroll === 'string'
                                ? parseFloat(reportData.summary.payroll)
                                : reportData.summary.payroll),
                            baseCurrency
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="h6" fontWeight="bold">
                          Net Profit / Loss
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={
                            (typeof reportData.summary.revenue === 'string'
                              ? parseFloat(reportData.summary.revenue)
                              : reportData.summary.revenue) -
                              ((typeof reportData.summary.expenses === 'string'
                                ? parseFloat(reportData.summary.expenses)
                                : reportData.summary.expenses) +
                                (typeof reportData.summary.payroll === 'string'
                                  ? parseFloat(reportData.summary.payroll)
                                  : reportData.summary.payroll)) >=
                            0
                              ? 'success.main'
                              : 'error.main'
                          }
                        >
                          {formatCurrency(
                            (typeof reportData.summary.revenue === 'string'
                              ? parseFloat(reportData.summary.revenue)
                              : reportData.summary.revenue) -
                              ((typeof reportData.summary.expenses === 'string'
                                ? parseFloat(reportData.summary.expenses)
                                : reportData.summary.expenses) +
                                (typeof reportData.summary.payroll === 'string'
                                  ? parseFloat(reportData.summary.payroll)
                                  : reportData.summary.payroll)),
                            baseCurrency
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default Accounting;
